import { baseUrlEndpoint, baseUrlPattern, resolveBaseUrl } from "../baseUrl";
import {
  MODELS_HELPER_TEXT,
  MODELS_PREFIX,
  openAiModelsFetch,
  withAllowedModelsHint,
} from "../models";
import { headerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";
import { openaiUsage } from "./usage";

const DEFAULT_OPENAI_BASE_URL = "https://api.openai.com/v1";

const openaiModelsFetch = openAiModelsFetch(
  "Fetch models from OpenAI",
  ({ baseUrl }) => baseUrlEndpoint(baseUrl, DEFAULT_OPENAI_BASE_URL, "/models")
);

export const openai = defineConnector({
  identifier: "openai",
  name: "OpenAI",
  tagLine: "AI models and assistants",
  logo,
  protocol: "http",
  tags: ["featured", "ai", "engineering"],
  defaultUrlPattern: ({ baseUrl }) =>
    baseUrlPattern(baseUrl, DEFAULT_OPENAI_BASE_URL),
  auth: (app) =>
    headerAuth([
      {
        name: "Authorization",
        value: app.secret.apiKey,
        scheme: "Bearer",
      },
      {
        name: "OpenAI-Organization",
        value: app.config.organizationId,
      },
    ]),
  steps: [
    {
      title: "Get an API key",
      items: [
        {
          kind: "markdown",
          content:
            "Visit the [OpenAI API keys page](https://platform.openai.com/api-keys) and create or copy an API key.",
        },
      ],
    },
    {
      title: "Copy and paste the API key into the field below",
      items: [
        {
          kind: "input",
          label: "OpenAI API key",
          placeholder: "sk-...",
          required: true,
          target: { kind: "secrets", field: "apiKey" },
        },
        {
          kind: "markdown",
          content:
            "If you belong to multiple organizations, you can optionally specify which organization's quota to use. [Learn more about authentication](https://platform.openai.com/docs/api-reference/authentication).",
        },
        {
          kind: "input",
          label: "Organization ID (optional)",
          placeholder: "org-...",
          target: { kind: "configuration", field: "organizationId" },
        },
        {
          kind: "url",
          label: "Base URL (optional)",
          placeholder: DEFAULT_OPENAI_BASE_URL,
          defaultValue: DEFAULT_OPENAI_BASE_URL,
          target: { kind: "configuration", field: "baseUrl" },
          helperText: "Leave blank for the default OpenAI API endpoint.",
        },
      ],
    },
    {
      title: "Choose available models (optional)",
      items: [
        {
          kind: "list",
          label: "Available models",
          prefix: MODELS_PREFIX,
          columns: [{ field: "name", placeholder: "gpt-5.6-sol" }],
          addLabel: "Add model",
          fetch: openaiModelsFetch,
          helperText: MODELS_HELPER_TEXT,
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl }) =>
      baseUrlEndpoint(baseUrl, DEFAULT_OPENAI_BASE_URL, "/models"),
  },
  publicEnvVars: ({ baseUrl }) => ({
    OPENAI_BASE_URL: resolveBaseUrl(baseUrl, DEFAULT_OPENAI_BASE_URL),
  }),
  usageHint: (configuration) =>
    withAllowedModelsHint(
      "Use process.env.OPENAI_BASE_URL as the OpenAI API base URL. Pass a placeholder API key; the proxy injects the real key from the connected app.",
      configuration
    ),
  usage: openaiUsage(),
});
